import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useExperienceStore } from '../store/experienceStore'
import { QUALITY_PRESETS } from '../config/quality'

export function ParticleField() {
  const pointsRef = useRef<THREE.Points>(null)
  const quality = useExperienceStore((s) => s.quality)
  const count = QUALITY_PRESETS[quality].particles

  const { positions, velocities, colors } = useMemo(() => {
    const positions = new Float32Array(count * 3)
    const velocities = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      const i3 = i * 3
      const r = 8 + Math.random() * 40
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      positions[i3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.4
      positions[i3 + 2] = r * Math.cos(phi)
      velocities[i3] = (Math.random() - 0.5) * 0.02
      velocities[i3 + 1] = (Math.random() - 0.5) * 0.02
      velocities[i3 + 2] = (Math.random() - 0.5) * 0.02
      const t = Math.random()
      colors[i3] = 0.4 + t * 0.6
      colors[i3 + 1] = 0.7 + t * 0.3
      colors[i3 + 2] = 1.0
    }
    return { positions, velocities, colors }
  }, [count])

  useFrame((_, dt) => {
    const pts = pointsRef.current
    if (!pts) return
    const { timeSpeed, traverseIntensity, palmNdc } = useExperienceStore.getState()
    const pos = pts.geometry.attributes.position.array as Float32Array
    const speed = Math.abs(timeSpeed)
    const dir = Math.sign(timeSpeed) || 1
    const trail = 0.5 + speed * 8 + traverseIntensity * 2

    const emitter = palmNdc
      ? new THREE.Vector3(palmNdc.x * 6, palmNdc.y * 4, 2)
      : new THREE.Vector3(0, -0.5, 2)

    for (let i = 0; i < count; i++) {
      const i3 = i * 3
      pos[i3] += velocities[i3] * trail * 60 * dt * dir
      pos[i3 + 1] += velocities[i3 + 1] * trail * 60 * dt
      pos[i3 + 2] += velocities[i3 + 2] * trail * 60 * dt

      // soft attract toward emitter when traversing
      if (traverseIntensity > 0.05) {
        pos[i3] += (emitter.x - pos[i3]) * 0.002 * traverseIntensity
        pos[i3 + 1] += (emitter.y - pos[i3 + 1]) * 0.002 * traverseIntensity
      }

      const d = Math.hypot(pos[i3], pos[i3 + 1], pos[i3 + 2])
      if (d > 55 || d < 2) {
        const r = 10 + Math.random() * 30
        const theta = Math.random() * Math.PI * 2
        const phi = Math.acos(2 * Math.random() - 1)
        pos[i3] = r * Math.sin(phi) * Math.cos(theta)
        pos[i3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.4
        pos[i3 + 2] = r * Math.cos(phi)
      }
    }
    pts.geometry.attributes.position.needsUpdate = true
    pts.rotation.y += dt * (0.02 + speed * 0.5)
  })

  return (
    <points ref={pointsRef} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.08}
        vertexColors
        transparent
        opacity={0.75}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        sizeAttenuation
      />
    </points>
  )
}
